import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import useFetchApi from '../../../../hooks/useFetchApi';
import {
  CultivationCyclePopulatedSchema,
  GetCultivationCyclesBySectorResponse,
  PhenologicalStateSectorResponse,
  PhenologicalStateWithCycle,
  PhenologicalStateWithId,
} from '../../../../requests/api/apiTypes';
import { CriticalPoint, PhaseLine } from './types';
import apiClient from '../../../../requests/api/apiClient';
import { validatePoints, getPhenologicalStateFromInterval } from './utils';
import { FetchError } from '../../../../views/types';
import { ReduxState } from '../../../../types';

const NOT_SUPPORTED_PHASE_LINES = [1, 2, 3];

const PhaseLineManager = (
  {
    height = 450, generalLines, onPhaseLinesUpdated,
  }: {
    height?: number,
    generalLines: CriticalPoint[],
    onPhaseLinesUpdated?: () => void,
  },
) => {
  const { enqueueSnackbar } = useSnackbar();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);

  const params = {
    farmId: queryParams.get('farmId') || '',
    sectorId: queryParams.get('sectorId') || '',
    wiseconnZoneId: queryParams.get('wiseconnZoneId') || '',
  };
  const [
    cultivationCycles, setCultivationCycles,
  ] = useState<CultivationCyclePopulatedSchema[] | undefined>(undefined);
  const [
    selectedCycle, setSelectedCycle,
  ] = useState<CultivationCyclePopulatedSchema | undefined>(undefined);
  const [
    selectedPhase, setSelectedPhase,
  ] = useState<PhenologicalStateWithId | undefined>(undefined);

  const [
    phenologicalStatesByCycle,
    setPhenologicalStatesByCycle] = useState<PhenologicalStateWithCycle | undefined>(undefined);

  const [availableLines, setAvailableLines] = useState<PhaseLine | undefined>(undefined);

  const [phaseCriticalPoints, setPhaseCriticalPoints] = useState<CriticalPoint[]>([]);

  const handlePhaseCriticalPointChange = (id: number, value?: number) => {
    setPhaseCriticalPoints(phaseCriticalPoints.map((point) => (
      point.id === id ? { ...point, value } : point
    )));
  };

  const updatePhaseLines = async () => {
    const maxValue = phaseCriticalPoints.slice(0)[0].value;
    const minValue = phaseCriticalPoints.slice(-1)[0].value;
    if (!validatePoints(phaseCriticalPoints, enqueueSnackbar, minValue, maxValue)) {
      return;
    }

    if (!selectedPhase) {
      return;
    }

    try {
      const { data: response } = await apiClient.phenologicalState.phenologicalStateUpdate(
        selectedPhase?.id,
        phaseCriticalPoints,
      );
      setAvailableLines({
        ...availableLines,
        [selectedCycle?.name as string]: {
          ...availableLines?.[selectedCycle?.name as string],
          [selectedPhase?.name as string]: phaseCriticalPoints,
        },
      });
      enqueueSnackbar(response.message, { variant: 'success' });
      if (onPhaseLinesUpdated) {
        onPhaseLinesUpdated();
      }
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const { data } = useFetchApi<GetCultivationCyclesBySectorResponse>({
    modelEndpoint: 'cultivationCycleSectorDetail',
    modelName: 'cultivationCycleSector',
    id: params.sectorId,
    dependencies: [params.sectorId],
  });

  const { data: dataPhenologicalStatesByCycle } = useFetchApi<PhenologicalStateSectorResponse>({
    modelEndpoint: 'phenologicalStateSectorList',
    modelName: 'phenologicalState',
    id: params.sectorId,
    dependencies: [params.sectorId],
  });

  useEffect(() => {
    if (data) {
      setCultivationCycles(data.cultivationCycles);
    }
  }, [data]);

  useEffect(() => {
    if (dataPhenologicalStatesByCycle) {
      setPhenologicalStatesByCycle(dataPhenologicalStatesByCycle.phenologicalStates);
    }
  }, [dataPhenologicalStatesByCycle]);

  useEffect(() => {
    if (
      !interval
      || !cultivationCycles
      || cultivationCycles.length === 0
      || !phenologicalStatesByCycle
    ) {
      return;
    }
    const intervalStart = dayjs(interval.startDate);
    const intervalEnd = dayjs(interval.endDate);

    const matchedCycle = cultivationCycles.find((cycle) => {
      const cStart = dayjs(cycle.startDate);
      const cEnd = dayjs(cycle.endDate);
      return !(cEnd.isBefore(intervalStart, 'day') || cStart.isAfter(intervalEnd, 'day'));
    }) || cultivationCycles[0];
    if (!matchedCycle) {
      return;
    }

    if (!selectedCycle || selectedCycle.id !== matchedCycle.id) {
      setSelectedCycle(matchedCycle);
    }

    const states = phenologicalStatesByCycle[matchedCycle.id] || [];
    if (states.length === 0) {
      return;
    }
    const matchedState = getPhenologicalStateFromInterval(states, interval) || states[0];
    if (!selectedPhase || selectedPhase.id !== matchedState.id) {
      setSelectedPhase(matchedState);
    }
  }, [interval, cultivationCycles, phenologicalStatesByCycle]);

  useEffect(() => {
    if (selectedCycle && selectedPhase) {
      setPhaseCriticalPoints(
        selectedPhase?.lines?.length
          ? selectedPhase?.lines.filter(
            (line) => !NOT_SUPPORTED_PHASE_LINES.includes(line.id),
          )
          : generalLines.filter(
            (line) => !NOT_SUPPORTED_PHASE_LINES.includes(line.id),
          ),
      );
    }
  }, [selectedCycle, selectedPhase, generalLines]);

  if (!data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}>
        <CircularProgress />
      </Box>
    );
  }
  return (
    <Card>
      <CardContent>
        <Box>
          <Typography variant="h5" fontWeight="bold" gutterBottom mb={2}>
            Configurar Líneas de Gestión por Estado Fenológico
          </Typography>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12}>
              <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
                <InputLabel>Ciclo de cultivo</InputLabel>
                <Select
                  value={selectedCycle?.id || ''}
                  onChange={(e) => {
                    const cycle = cultivationCycles?.find(
                      (c) => c.id === e.target.value,
                    );
                    setSelectedCycle(cycle);
                    setSelectedPhase(undefined);
                  }}
                >
                  {cultivationCycles?.map((cycle: CultivationCyclePopulatedSchema) => (
                    <MenuItem key={cycle.id} value={cycle.id}>{cycle.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl variant="standard" fullWidth>
                <InputLabel>Estado fenológico</InputLabel>
                <Select
                  value={selectedPhase?.name || ''}
                  onChange={(e) => {
                    const phase = phenologicalStatesByCycle?.[selectedCycle?.id || '']?.find(
                      (s: PhenologicalStateWithId) => s.name === e.target.value,
                    );
                    setSelectedPhase(phase);
                  }}
                >
                  {phenologicalStatesByCycle?.[selectedCycle?.id || '']?.map((state: PhenologicalStateWithId) => (
                    <MenuItem key={state.id} value={state.name}>{state.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {selectedPhase && (
              <Grid item xs={12}>
                <DatePicker
                  sx={{ mb: 2 }}
                  disabled={true}
                  label="Fecha de inicio"
                  value={dayjs(selectedPhase?.startDate).toDate()}
                />
                <DatePicker
                  sx={{ mb: 2 }}
                  disabled={true}
                  label="Fecha de fin"
                  value={dayjs(selectedPhase?.endDate).toDate()}
                />
                {phaseCriticalPoints.map((point) => (
                  <TextField
                    key={point.id}
                    fullWidth
                    type="number"
                    label={point.customLabel}
                    value={point.value ?? ''}
                    onChange={(e) => handlePhaseCriticalPointChange(
                      point.id,
                      e.target.value === '' ? undefined : Number(e.target.value),
                    )}
                    variant="standard"
                  />
                ))}
              </Grid>
            )}
          </Grid>

          {selectedPhase && (
            <Grid item xs={12}>
              <Button
                variant="contained"
                fullWidth
                onClick={updatePhaseLines}
              >
                Actualizar Líneas de Gestión por Estado Fenológico
              </Button>
            </Grid>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PhaseLineManager;
